import { comparablePlayerName } from './same-player-name';

export interface PlayerNameParts {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
}

export type PlayerSortMode = 'custom' | 'number' | 'lastName' | 'firstName' | 'role';

export const PLAYER_SORT_MODES: readonly PlayerSortMode[] = [
  'custom',
  'number',
  'lastName',
  'firstName',
  'role',
] as const;

export function buildPlayerDisplayName(parts: PlayerNameParts): string {
  const first = (parts.firstName ?? '').trim();
  const last = (parts.lastName ?? '').trim();
  const joined = [first, last].filter(Boolean).join(' ');
  if (joined) return joined;
  return (parts.name ?? '').trim();
}

export function hasPlayerName(parts: PlayerNameParts): boolean {
  return Boolean(
    (parts.firstName ?? '').trim() ||
      (parts.lastName ?? '').trim() ||
      (parts.name ?? '').trim(),
  );
}

export function splitLegacyName(fullName: string | null | undefined): { firstName: string; lastName: string } {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  const [first, ...rest] = parts;
  return {
    firstName: first ?? '',
    lastName: rest.join(' '),
  };
}

const ROLE_SORT_ORDER: Record<string, number> = {
  Passeur: 0,
  Pointu: 1,
  R4: 2,
  Central: 3,
  Lib: 4,
};

function normalizeText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

type SortablePlayer = PlayerNameParts & {
  number?: number | null;
  role?: string | null;
};

export function sortRoster<T extends SortablePlayer>(roster: readonly T[], mode: PlayerSortMode): T[] {
  if (mode === 'custom') return [...roster];

  const indexed = roster.map((player, index) => ({ player, index }));

  indexed.sort((a, b) => {
    switch (mode) {
      case 'number': {
        const an = a.player.number ?? Number.POSITIVE_INFINITY;
        const bn = b.player.number ?? Number.POSITIVE_INFINITY;
        if (an !== bn) return an - bn;
        return a.index - b.index;
      }
      case 'lastName': {
        const al = normalizeText(a.player.lastName) || normalizeText(buildPlayerDisplayName(a.player));
        const bl = normalizeText(b.player.lastName) || normalizeText(buildPlayerDisplayName(b.player));
        const cmp = al.localeCompare(bl, 'fr', { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return a.index - b.index;
      }
      case 'firstName': {
        const af = normalizeText(a.player.firstName) || normalizeText(buildPlayerDisplayName(a.player));
        const bf = normalizeText(b.player.firstName) || normalizeText(buildPlayerDisplayName(b.player));
        const cmp = af.localeCompare(bf, 'fr', { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return a.index - b.index;
      }
      case 'role': {
        const ar = ROLE_SORT_ORDER[a.player.role ?? ''] ?? 99;
        const br = ROLE_SORT_ORDER[b.player.role ?? ''] ?? 99;
        if (ar !== br) return ar - br;
        return a.index - b.index;
      }
      default:
        return a.index - b.index;
    }
  });

  return indexed.map((entry) => entry.player);
}

/**
 * Live capture keys players by name: two "Enzo" in a match roster became one
 * player (a libero taken off came back as his R4 homonym). Homonyms get their
 * jersey number appended ("Enzo 17"), unique within a team; the others keep
 * their name. The backend replay reads these roster names by player id.
 */
export function disambiguateHomonyms<T extends { name: string; number?: number | null }>(players: T[]): T[] {
  const counts = new Map<string, number>();
  for (const p of players) counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
  let unnumbered = 0;
  return players.map((p) => {
    if ((counts.get(p.name) ?? 0) < 2) return p;
    const suffix = p.number != null ? String(p.number) : `(${++unnumbered})`;
    return { ...p, name: `${p.name} ${suffix}` };
  });
}

function homonymKey(parts: PlayerNameParts): string {
  return comparablePlayerName(buildPlayerDisplayName(parts));
}

/**
 * Names carried by two players or more in a team. Blocked at save: the coach
 * tells them apart (last name, initial) instead of the match adding numbers.
 */
export function findDuplicatePlayerNames(roster: readonly PlayerNameParts[]): string[] {
  const seen = new Map<string, string>();
  const duplicates = new Map<string, string>();
  for (const player of roster) {
    const key = homonymKey(player);
    if (!key) continue;
    const first = seen.get(key);
    if (first !== undefined) duplicates.set(key, first);
    else seen.set(key, buildPlayerDisplayName(player));
  }
  return [...duplicates.values()];
}

export function isDuplicatePlayerName(player: PlayerNameParts, duplicates: readonly string[]): boolean {
  const key = homonymKey(player);
  return key !== '' && duplicates.some((name) => homonymKey({ name }) === key);
}
